import { useEffect, useState } from "react";
import {
    X,
    Send,
    MessageCircle,
    MoreVertical,
    Pencil,
    Trash2
} from "lucide-react";

export default function CommentDrawer({
    post,
    comments = [],
    loading = false,

    onClose,
    onSend,
    onDelete,
    onEdit
}) {

    const [content, setContent] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [editingCommentId, setEditingCommentId] =
        useState(null);

    const [editingContent, setEditingContent] =
        useState("");

    const [openMenuId, setOpenMenuId] =
        useState(null);


    useEffect(() => {

        if (!post) {
            setContent("");
            setEditingCommentId(null);
            setEditingContent("");
            setOpenMenuId(null);
        }

    }, [post]);


    if (!post) {
        return null;
    }


    const handleSubmit = async (e) => {

        e.preventDefault();

        const trimmedContent =
            content.trim();

        if (!trimmedContent || submitting) {
            return;
        }

        try {

            setSubmitting(true);

            await onSend(trimmedContent);

            setContent("");

        } catch (error) {

            console.error(
                "Failed to add comment:",
                error
            );

        } finally {

            setSubmitting(false);

        }

    };


    const handleDelete = async (commentId) => {

        try {

            await onDelete(commentId);

            setOpenMenuId(null);

        } catch (error) {

            console.error(
                "Failed to delete comment:",
                error
            );

        }

    };


    const startEditing = (comment) => {

        setEditingCommentId(comment.id);

        setEditingContent(
            comment.content || ""
        );

        setOpenMenuId(null);

    };


    const cancelEditing = () => {

        setEditingCommentId(null);

        setEditingContent("");

    };


    const handleEdit = async (commentId) => {

        const trimmedContent =
            editingContent.trim();

        if (!trimmedContent) {
            return;
        }

        try {

            await onEdit(
                commentId,
                trimmedContent
            );

            cancelEditing();

        } catch (error) {

            console.error(
                "Failed to update comment:",
                error
            );

        }

    };


    return (

        <div className="fixed inset-0 z-[100]">

            {/* BACKDROP */}

            <div
                className="
                    absolute
                    inset-0
                    bg-black/40
                    backdrop-blur-sm
                "
                onClick={onClose}
            />


            {/* DRAWER */}

            <div
                className="
                    absolute
                    right-0
                    top-0
                    h-full
                    w-full
                    sm:w-[460px]
                    bg-white
                    shadow-2xl
                    flex
                    flex-col
                "
            >

                {/* HEADER */}

                <div
                    className="
                        flex
                        items-center
                        justify-between
                        px-5
                        py-4
                        border-b
                        border-gray-100
                        shrink-0
                    "
                >

                    <div className="flex items-center gap-3">

                        <div
                            className="
                                w-10
                                h-10
                                rounded-full
                                bg-gray-100
                                flex
                                items-center
                                justify-center
                            "
                        >
                            <MessageCircle
                                size={20}
                                className="text-gray-600"
                            />
                        </div>

                        <div>

                            <h2
                                className="
                                    text-lg
                                    font-semibold
                                    text-gray-900
                                "
                            >
                                Comments
                            </h2>

                            <p
                                className="
                                    text-xs
                                    text-gray-500
                                "
                            >
                                {comments.length}{" "}
                                {comments.length === 1
                                    ? "comment"
                                    : "comments"}
                            </p>

                        </div>

                    </div>


                    <button
                        type="button"
                        onClick={onClose}
                        className="
                            w-9
                            h-9
                            rounded-full
                            flex
                            items-center
                            justify-center
                            text-gray-500
                            hover:bg-gray-100
                            hover:text-gray-900
                            transition
                        "
                    >
                        <X size={20} />
                    </button>

                </div>


                {/* POST PREVIEW */}

                <div
                    className="
                        px-5
                        py-4
                        border-b
                        border-gray-100
                        bg-gray-50/70
                    "
                >

                    <p
                        className="
                            text-sm
                            text-gray-700
                            line-clamp-3
                        "
                    >
                        {post.content}
                    </p>

                </div>


                {/* COMMENTS */}

                <div
                    className="
                        flex-1
                        overflow-y-auto
                        px-5
                        py-5
                    "
                >

                    {loading ? (

                        <div
                            className="
                                flex
                                flex-col
                                items-center
                                justify-center
                                py-16
                                text-gray-400
                            "
                        >

                            <div
                                className="
                                    w-8
                                    h-8
                                    border-2
                                    border-gray-200
                                    border-t-gray-700
                                    rounded-full
                                    animate-spin
                                    mb-3
                                "
                            />

                            <p className="text-sm">
                                Loading comments...
                            </p>

                        </div>

                    ) : comments.length === 0 ? (

                        <div
                            className="
                                flex
                                flex-col
                                items-center
                                justify-center
                                py-16
                                text-center
                            "
                        >

                            <div
                                className="
                                    w-14
                                    h-14
                                    rounded-full
                                    bg-gray-100
                                    flex
                                    items-center
                                    justify-center
                                    mb-4
                                "
                            >
                                <MessageCircle
                                    size={25}
                                    className="text-gray-400"
                                />
                            </div>

                            <h3
                                className="
                                    text-sm
                                    font-semibold
                                    text-gray-800
                                "
                            >
                                No comments yet
                            </h3>

                            <p
                                className="
                                    mt-1
                                    text-xs
                                    text-gray-500
                                "
                            >
                                Be the first to share your thoughts.
                            </p>

                        </div>

                    ) : (

                        <div className="space-y-5">

                            {comments.map((comment) => (

                                <div
                                    key={comment.id}
                                    className="
                                        flex
                                        gap-3
                                    "
                                >

                                    {/* AVATAR */}

                                    <div className="shrink-0">

                                        {comment.profileImage ? (

                                            <img
                                                src={
                                                    comment.profileImage
                                                }
                                                alt={
                                                    comment.username ||
                                                    "User"
                                                }
                                                className="
                                                    w-9
                                                    h-9
                                                    rounded-full
                                                    object-cover
                                                "
                                            />

                                        ) : (

                                            <div
                                                className="
                                                    w-9
                                                    h-9
                                                    rounded-full
                                                    bg-gray-200
                                                    flex
                                                    items-center
                                                    justify-center
                                                    text-xs
                                                    font-semibold
                                                    text-gray-600
                                                "
                                            >
                                                {(
                                                    comment.username ||
                                                    "U"
                                                )
                                                    .charAt(0)
                                                    .toUpperCase()}
                                            </div>

                                        )}

                                    </div>


                                    {/* COMMENT BODY */}

                                    <div className="flex-1 min-w-0">

                                        <div
                                            className="
                                                flex
                                                items-start
                                                justify-between
                                                gap-2
                                            "
                                        >

                                            <div>

                                                <p
                                                    className="
                                                        text-sm
                                                        font-semibold
                                                        text-gray-900
                                                    "
                                                >
                                                    {comment.username ||
                                                        "User"}
                                                </p>

                                            </div>


                                            {/* MENU */}

                                            <div className="relative">

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setOpenMenuId(
                                                            openMenuId ===
                                                                comment.id
                                                                ? null
                                                                : comment.id
                                                        )
                                                    }
                                                    className="
                                                        w-7
                                                        h-7
                                                        rounded-full
                                                        flex
                                                        items-center
                                                        justify-center
                                                        text-gray-400
                                                        hover:bg-gray-100
                                                    "
                                                >
                                                    <MoreVertical
                                                        size={16}
                                                    />
                                                </button>


                                                {openMenuId ===
                                                    comment.id && (

                                                    <div
                                                        className="
                                                            absolute
                                                            right-0
                                                            top-8
                                                            z-30
                                                            w-32
                                                            bg-white
                                                            border
                                                            border-gray-100
                                                            rounded-xl
                                                            shadow-xl
                                                            overflow-hidden
                                                        "
                                                    >

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                startEditing(
                                                                    comment
                                                                )
                                                            }
                                                            className="
                                                                w-full
                                                                px-3
                                                                py-2
                                                                flex
                                                                items-center
                                                                gap-2
                                                                text-sm
                                                                text-gray-700
                                                                hover:bg-gray-50
                                                            "
                                                        >
                                                            <Pencil
                                                                size={14}
                                                            />
                                                            Edit
                                                        </button>


                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    comment.id
                                                                )
                                                            }
                                                            className="
                                                                w-full
                                                                px-3
                                                                py-2
                                                                flex
                                                                items-center
                                                                gap-2
                                                                text-sm
                                                                text-red-600
                                                                hover:bg-red-50
                                                            "
                                                        >
                                                            <Trash2
                                                                size={14}
                                                            />
                                                            Delete
                                                        </button>

                                                    </div>

                                                )}

                                            </div>

                                        </div>


                                        {editingCommentId ===
                                        comment.id ? (

                                            <div className="mt-2">

                                                <textarea
                                                    value={
                                                        editingContent
                                                    }
                                                    onChange={(e) =>
                                                        setEditingContent(
                                                            e.target.value
                                                        )
                                                    }
                                                    rows={3}
                                                    autoFocus
                                                    className="
                                                        w-full
                                                        rounded-xl
                                                        border
                                                        border-gray-200
                                                        px-3
                                                        py-2
                                                        text-sm
                                                        resize-none
                                                        outline-none
                                                        focus:border-gray-400
                                                    "
                                                />

                                                <div
                                                    className="
                                                        flex
                                                        justify-end
                                                        gap-2
                                                        mt-2
                                                    "
                                                >

                                                    <button
                                                        type="button"
                                                        onClick={
                                                            cancelEditing
                                                        }
                                                        className="
                                                            px-3
                                                            py-1.5
                                                            text-xs
                                                            font-medium
                                                            text-gray-600
                                                            rounded-lg
                                                            hover:bg-gray-100
                                                        "
                                                    >
                                                        Cancel
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleEdit(
                                                                comment.id
                                                            )
                                                        }
                                                        disabled={
                                                            !editingContent.trim()
                                                        }
                                                        className="
                                                            px-3
                                                            py-1.5
                                                            text-xs
                                                            font-medium
                                                            text-white
                                                            bg-gray-900
                                                            rounded-lg
                                                            disabled:opacity-40
                                                        "
                                                    >
                                                        Save
                                                    </button>

                                                </div>

                                            </div>

                                        ) : (

                                            <p
                                                className="
                                                    mt-1
                                                    text-sm
                                                    text-gray-700
                                                    whitespace-pre-wrap
                                                    break-words
                                                "
                                            >
                                                {comment.content}
                                            </p>

                                        )}


                                        {/* META */}

                                        <div
                                            className="
                                                mt-2
                                                flex
                                                items-center
                                                gap-3
                                                text-xs
                                                text-gray-400
                                            "
                                        >

                                            {comment.createdAt && (

                                                <span>
                                                    {formatDate(
                                                        comment.createdAt
                                                    )}
                                                </span>

                                            )}

                                            <span>
                                                {comment.likes || 0}{" "}
                                                likes
                                            </span>

                                        </div>

                                    </div>

                                </div>

                            ))}

                        </div>

                    )}

                </div>


                {/* ADD COMMENT */}

                <form
                    onSubmit={handleSubmit}
                    className="
                        shrink-0
                        border-t
                        border-gray-100
                        p-4
                        bg-white
                    "
                >

                    <div
                        className="
                            flex
                            items-end
                            gap-2
                            rounded-2xl
                            border
                            border-gray-200
                            bg-gray-50
                            p-2
                            focus-within:border-gray-400
                        "
                    >

                        <textarea
                            value={content}
                            onChange={(e) =>
                                setContent(e.target.value)
                            }
                            placeholder="Write a comment..."
                            rows={1}
                            maxLength={1000}
                            disabled={submitting}
                            className="
                                flex-1
                                bg-transparent
                                resize-none
                                outline-none
                                px-2
                                py-2
                                text-sm
                                text-gray-800
                                placeholder:text-gray-400
                            "
                        />


                        <button
                            type="submit"
                            disabled={
                                submitting ||
                                !content.trim()
                            }
                            className="
                                w-9
                                h-9
                                rounded-xl
                                bg-gray-900
                                text-white
                                flex
                                items-center
                                justify-center
                                shrink-0
                                transition
                                hover:bg-gray-800
                                disabled:opacity-40
                                disabled:cursor-not-allowed
                            "
                        >

                            {submitting ? (

                                <div
                                    className="
                                        w-4
                                        h-4
                                        border-2
                                        border-white/40
                                        border-t-white
                                        rounded-full
                                        animate-spin
                                    "
                                />

                            ) : (

                                <Send size={16} />

                            )}

                        </button>

                    </div>

                    <div
                        className="
                            flex
                            justify-end
                            mt-1
                            px-1
                        "
                    >

                        <span className="text-[10px] text-gray-400">
                            {content.length}/1000
                        </span>

                    </div>

                </form>

            </div>

        </div>

    );
}


function formatDate(date) {

    const value = new Date(date);

    if (Number.isNaN(value.getTime())) {
        return "";
    }

    return value.toLocaleDateString(
        undefined,
        {
            month: "short",
            day: "numeric",
            year: "numeric"
        }
    );

}